/**
 * Allowlist sanitizer for the rich summary MeetGeek stores in `meet_geek.summary`.
 *
 * That column is not plain text: it is a small document with headings, lists and
 * deep links back into the MeetGeek transcript, so it has to be rendered as HTML
 * rather than escaped. It is also third-party output derived from what people
 * said in a meeting, which makes it untrusted input at a trust boundary — a
 * meeting title or a spoken line is enough to smuggle markup through.
 *
 * ponytail: allowlist over the browser's own parser instead of adding a sanitizer
 * dependency. The parse happens in an inert document, so nothing runs and no
 * resource is fetched while we walk it.
 */

// Tag -> attributes that survive. Everything else is dropped or unwrapped.
const ALLOWED = {
  P: [],
  BR: [],
  STRONG: [],
  B: [],
  EM: [],
  I: [],
  U: [],
  UL: [],
  OL: [],
  LI: [],
  A: ['href', 'title'],
};

// Unwrapping these would leak their text content into the page (a <script> body
// would render as visible source), so they go entirely, children included.
const DROP = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'NOSCRIPT', 'TEMPLATE']);

// javascript: and data: are the reason href is filtered rather than just kept.
const SAFE_HREF = /^(?:https?:|mailto:)/i;

function clean(node) {
  const children = Array.prototype.slice.call(node.childNodes);
  children.forEach((child) => {
    if (child.nodeType === 3) return; // text, nothing to do
    if (child.nodeType !== 1) return child.remove(); // comments, CDATA, ...

    if (DROP.has(child.tagName)) return child.remove();

    clean(child);

    const allowedAttrs = ALLOWED[child.tagName];
    if (!allowedAttrs) {
      // Unknown-but-harmless wrapper (div, span, table...): keep the text, lose
      // the element, so stripping a tag never silently deletes prose.
      child.replaceWith(...child.childNodes);
      return;
    }

    Array.prototype.slice.call(child.attributes).forEach((attr) => {
      if (allowedAttrs.indexOf(attr.name.toLowerCase()) === -1) {
        child.removeAttribute(attr.name);
      }
    });

    if (child.tagName === 'A') {
      const href = child.getAttribute('href') || '';
      // MeetGeek emits `mailto:` with an empty address for unresolved mentions;
      // those are not links, just decoration around a name.
      if (!SAFE_HREF.test(href) || href === 'mailto:') {
        child.removeAttribute('href');
      } else {
        child.setAttribute('target', '_blank');
        child.setAttribute('rel', 'noopener noreferrer');
      }
    }
  });
}

// `parser` is only injected by the test — the browser's own DOMParser otherwise.
// ponytail: CommonJS like meetingRatings.js, so `node` runs its test with no
// jest/babel setup; the ESM component imports it fine via babel interop.
function sanitizeHtml(html, parser) {
  if (!html) return '';
  const p = parser || new DOMParser();
  const doc = p.parseFromString(String(html), 'text/html');
  clean(doc.body);
  return doc.body.innerHTML;
}

module.exports = { sanitizeHtml };
